// Single source of truth for the wizard's step ↔ URL mapping.
// URLs look like /self-service/:slug/start/:stepSlug — this maps that slug to
// the numeric step the wizard uses internally.

export type WizardStep = 1 | 2 | 3 | 4 | 5;

export const WIZARD_STEP_SLUGS: Record<WizardStep, string> = {
  1: 'business',
  2: 'campaign',
  3: 'video',
  4: 'verify',
  5: 'review',
};

export const SLUG_TO_STEP: Record<string, WizardStep> = {
  business: 1,
  campaign: 2,
  video: 3,
  verify: 4,
  review: 5,
};

export const MAX_STEP: WizardStep = 5;

export function stepFromSlug(slug: string | undefined): WizardStep | null {
  if (!slug) return null;
  return SLUG_TO_STEP[slug] ?? null;
}

export function slugFromStep(step: WizardStep): string {
  return WIZARD_STEP_SLUGS[step];
}

// CTV-471: a signed-in user has already proven their email via their Supabase
// session, so the Verify step (4) is skipped — forward/back navigation jumps
// straight over it. These helpers are the single source of truth for that skip
// so handleNext/handleBack stay in sync.
export function nextWizardStep(current: WizardStep, skipVerify: boolean): WizardStep {
  if (current >= MAX_STEP) return current;
  const next = (current + 1) as WizardStep;
  if (next === 4 && skipVerify) return 5;
  return next;
}

export function prevWizardStep(current: WizardStep, skipVerify: boolean): WizardStep {
  if (current <= 1) return current;
  const prev = (current - 1) as WizardStep;
  if (prev === 4 && skipVerify) return 3;
  return prev;
}

// The number to show the user for a given step. When Verify (4) is skipped, the
// Review step renumbers from 5 to 4; steps before Verify are unaffected. Used
// by the step headers so the visible numbering matches the progress bar.
export function displayStepNumber(step: WizardStep, skipVerify: boolean): number {
  return skipVerify && step > 4 ? step - 1 : step;
}

// The step a buyer should land on when a mid-wizard seat switch reloads a
// different advertiser's draft. `savedStep` is the draft's persisted
// `current_step`; a missing draft (nullish) means the new seat has no work in
// progress, so start at step 1. Out-of-range values are clamped into
// 1..MAX_STEP. Verify (4) is never a landing step for a skip-verify user, so a
// legacy draft parked there resumes at Review (5) rather than a step the
// wizard would immediately bounce them off. See the seat-switch redirect in
// SelfServiceWizard.
export function resumeStepFromDraft(
  savedStep: number | undefined | null,
  skipVerify: boolean,
): WizardStep {
  if (savedStep == null || !Number.isFinite(savedStep)) return 1;
  const clamped = Math.max(1, Math.min(MAX_STEP, Math.trunc(savedStep))) as WizardStep;
  if (clamped === 4 && skipVerify) return 5;
  return clamped;
}
